import { createReducer, on } from '@ngrx/store';
import { FullscreenLayoutTemplate } from '@src/assets/css/fullscreen.template.scss';
import { SearchType } from '@src/suite/common/enums/enums';
import { ObjectTypes } from '@src/suite/common/enums/object-types';
import {
    FourthSidecarActions,
    FullscreenActions,
    MainSidecarActions,
    OpenFullscreenModalActions,
    SecondSidecarActions,
    ThirdSidecarActions,
} from '@src/suite/common/store/fullscreen/actions/fullscreen.actions';
import { FullscreenNewRecordState, FullscreenState, SidecarMenu, SidecarState } from '@src/suite/common/store/fullscreen/models/fullscreen.models';
import { PlaybookApiActions } from '@src/suite/common/store/playbook/playbook.actions';
import { RecordHttpActions } from '@src/suite/common/store/records/records.actions';
import { StoryHttpActions } from '@src/suite/common/store/story/actions/story.actions';
import { updateReadOnlyCriteria, updateVisibility } from '@src/suite/common/utils/record-utils';

const DEFAULT_MOS_DEVICE_ID = 'PILOT';

export const initialState: FullscreenState = {
    template: FullscreenLayoutTemplate.ONLY_MAIN,
    recordGuid: null,
    /**
     * This is the recordGuid from the curren view list, that is loaded behind the fullscreen dialog.
     */
    currentViewRecordGuid: null,
    recordType: ObjectTypes.UNASSIGNED,
    recordHasUnsavedChanges: false,
    /**
     * This is used when we have a view of stories, and we need to open the story as standalone.
     * That means we can't go back to the parent topic (if there is one) on the fullscreen dialog
     */
    standaloneStory: false,
    pinnedItemGuids: [],
    selectedPinnedItemGuid: null,
    searchResultGuids: [],
    searchTerm: '',
    showLoader: false,
    treeDataPair: null,
};

export const fullscreenReducer = createReducer(
    initialState,
    on(RecordHttpActions.saveRecordChanges, PlaybookApiActions.createStoryFromTemplate, StoryHttpActions.generateNewVersion, (state): FullscreenState => {
        return {
            ...state,
            showLoader: !state.showLoader,
        };
    }),
    on(FullscreenActions.setLoadingProgressBar, (state, { showLoader }): FullscreenState => {
        return {
            ...state,
            showLoader: showLoader,
        };
    }),
    on(
        OpenFullscreenModalActions.openRundownFullscreenDialog,
        OpenFullscreenModalActions.openStoryFullscreenDialog,
        OpenFullscreenModalActions.openTopicFullscreenDialog,
        FullscreenActions.openTopic,
        FullscreenActions.openStory,
        FullscreenActions.openRundown,
        (state, { recordGuid, recordType, fromPinnedItem }): FullscreenState => ({
            ...state,
            recordGuid,
            recordType,
            selectedPinnedItemGuid: fromPinnedItem ? recordGuid : null,
            recordHasUnsavedChanges: false,
        })
    ),
    on(
        OpenFullscreenModalActions.openTopicFullscreenDialog,
        OpenFullscreenModalActions.openRundownFullscreenDialog,
        (state, { recordGuid }): FullscreenState => ({
            ...state,
            currentViewRecordGuid: recordGuid,
            standaloneStory: false,
        })
    ),
    on(
        OpenFullscreenModalActions.openStoryFullscreenDialog,
        (state, { listGuid, standaloneStory }): FullscreenState => ({
            ...state,
            standaloneStory: standaloneStory,
            currentViewRecordGuid: listGuid,
        })
    ),
    on(
        OpenFullscreenModalActions.openRundownFullscreenDialog,
        (state): FullscreenState => ({
            ...state,
            template: FullscreenLayoutTemplate.ONLY_MAIN,
        })
    ),
    on(
        OpenFullscreenModalActions.openTopicFullscreenDialog,
        (state, action): FullscreenState => ({
            ...state,
            currentViewRecordGuid: action.recordGuid,
        })
    ),
    on(
        OpenFullscreenModalActions.openRundownFullscreenDialog,
        OpenFullscreenModalActions.openStoryFullscreenDialog,
        OpenFullscreenModalActions.openTopicFullscreenDialog,
        OpenFullscreenModalActions.openAssignmentFullscreenDialog,
        (state, { treeDataPair }): FullscreenState => {
            if (treeDataPair) {
                return {
                    ...state,
                    treeDataPair,
                };
            }
            return state;
        }
    ),
    on(
        FullscreenActions.resetState,
        (): FullscreenState => ({
            ...initialState,
        })
    ),
    on(FullscreenActions.setFullscreenTemplate, (state, { template }): FullscreenState => {
        return { ...state, template };
    }),
    on(FullscreenActions.pinItem, (state, { itemGuid }): FullscreenState => {
        const uniquePinnedItemGuids = new Set([...state.pinnedItemGuids, itemGuid]);
        return {
            ...state,
            pinnedItemGuids: [...(uniquePinnedItemGuids.values() || [])],
        };
    }),
    on(FullscreenActions.unpinItem, (state, { itemGuid }): FullscreenState => {
        return {
            ...state,
            pinnedItemGuids: state.pinnedItemGuids.filter(guid => guid !== itemGuid),
        };
    }),
    on(FullscreenActions.setSearchTerm, (state, { searchTerm }): FullscreenState => {
        return {
            ...state,
            searchTerm,
        };
    }),
    on(FullscreenActions.setSearchResultsGuids, (state, { guids }): FullscreenState => {
        return {
            ...state,
            searchResultGuids: guids,
        };
    }),
    on(FullscreenActions.setCurrentRecordHasUnsavedChanges, (state, { hasUnsavedChanges }): FullscreenState => {
        return {
            ...state,
            recordHasUnsavedChanges: hasUnsavedChanges,
        };
    })
);

export const newRecordState: FullscreenNewRecordState = {
    record: null,
    selectedStoryIndex: -1,
    recordTemplateGuid: null,
    createInGroupData: null,
    recordCreating: false,
    wireRecord: null,
};

export const fullscreenNewRecordReducer = createReducer(
    newRecordState,
    on(OpenFullscreenModalActions.openCreateNewRecordFullscreen, (_, action): FullscreenNewRecordState => {
        updateVisibility(action.record, true);
        updateReadOnlyCriteria(action.record);
        return {
            ...action,
        };
    }),
    on(
        FullscreenActions.resetState,
        (): FullscreenNewRecordState => ({
            ...newRecordState,
        })
    ),
    on(FullscreenActions.addNewAssignmentToNewRecord, (state, action): FullscreenNewRecordState => {
        const newRecord = { ...state.record } as StorytellingTopicRecord;

        const assignmentToAdd = { ...action.assignment };
        assignmentToAdd.isNewRecord = true;
        newRecord.children = [...(newRecord.children || []), assignmentToAdd];
        newRecord.Sequence = newRecord.children.map(child => child.Guid);
        return {
            ...state,
            record: newRecord,
        };
    }),
    on(FullscreenActions.removeNewRecordItems, (state, action): FullscreenNewRecordState => {
        const newRecord = { ...state.record } as StorytellingTopicRecord;
        const recordToRemove = { ...action.record };
        if (recordToRemove.Type === ObjectTypes.ASSIGNMENT) {
            newRecord.Sequence = newRecord.Sequence.filter(assignmentGuid => assignmentGuid !== recordToRemove.Guid);
            newRecord.children = newRecord.children.filter(child => child.Guid !== recordToRemove.Guid);
        } else {
            newRecord.stories = newRecord.stories.filter(story => story.Guid !== recordToRemove.Guid);
        }
        return {
            ...state,
            record: newRecord,
        };
    }),
    on(FullscreenActions.addNewStoriesToNewRecord, (state, action): FullscreenNewRecordState => {
        const newRecord = { ...state.record } as StorytellingTopicRecord;

        const newStories = (action.stories || []).map(story => {
            // makes blank story template editable in new topic record
            if (!story?.text) {
                story.text = '';
            }
            return {
                ...story,
                isNewRecord: true,
            };
        });
        newRecord.stories = [...(newRecord.stories || []), ...newStories];
        return {
            ...state,
            record: newRecord,
        };
    }),
    on(FullscreenActions.openStory, (state, action): FullscreenNewRecordState => {
        if (state.record) {
            const storyIdx = (state?.record as StorytellingTopicRecord)?.stories?.findIndex(story => story.Guid === action.recordGuid);
            return {
                ...state,
                selectedStoryIndex: storyIdx,
            };
        }

        return state;
    }),
    on(FullscreenActions.closeCurrentStory, (state): FullscreenNewRecordState => {
        if (state.record) {
            return {
                ...state,
                selectedStoryIndex: -1,
            };
        }

        return state;
    }),
    on(FullscreenActions.updateCurrentNewRecord, (state, { record }): FullscreenNewRecordState => {
        updateVisibility(record);
        updateReadOnlyCriteria(record);
        return {
            ...state,
            record,
        };
    }),
    on(FullscreenActions.resetCurrentNewRecord, (state): FullscreenNewRecordState => {
        return {
            ...state,
            recordCreating: false,
            selectedStoryIndex: -1,
        };
    }),
    on(PlaybookApiActions.createTopic, PlaybookApiActions.createStory, (state): FullscreenNewRecordState => {
        return {
            ...state,
            recordCreating: true,
        };
    }),
    on(PlaybookApiActions.createRecordDone, (state): FullscreenNewRecordState => {
        return {
            ...state,
            recordCreating: false,
        };
    })
);

const BASE_SIDECAR_INITIAL_STATE: SidecarState = {
    selected: null,
    selectedStoryHasUnsavedChanges: false,
    selectedAssignmentGuid: null,
    storedAssignmentViewStates: null,
    currentAssignmentViewStateGuid: null,
    selectedAssignmentListSearchType: SearchType.SAVED,
    selectedStoryGuid: null,
    storedStoryViewStates: null,
    currentStoryViewStateGuid: null,
    selectedStoryListSearchType: SearchType.SAVED,
    selectedTopicGuid: null,
    storedTopicViewStates: null,
    currentTopicViewStateGuid: null,
    selectedTopicListSearchType: SearchType.SAVED,
    selectedRundownGuid: null,
    storedRundownViewStates: null,
    currentRundownViewStateGuid: null,
    selectedRundownListSearchType: SearchType.SAVED,
    assignmentListGuids: null,
    storyListGuids: null,
    topicListGuids: null,
    rundownListGuids: null,
    currentRundownSelectedStoryGuid: null,
    currentTopicSelectedStoryGuid: null,
    currentTopicSelectedAssignmentGuid: null,
    selectedMosItemID: DEFAULT_MOS_DEVICE_ID,
};

// Sidecar reducers
// Main sidecar reducer
export const mainSidecarInitialState: SidecarState = {
    ...BASE_SIDECAR_INITIAL_STATE,
};

export const mainSidecarReducer = createReducer(
    mainSidecarInitialState,
    on(
        FullscreenActions.resetState,
        (): SidecarState => ({
            ...secondSidecarInitialState,
        })
    ),
    on(
        MainSidecarActions.selectSidecarMenu,
        (state, { menu }): SidecarState => ({
            ...state,
            selected: menu,
        })
    ),
    on(
        MainSidecarActions.selectSidecarAssignment,
        (state, { assignmentGuid }): SidecarState => ({
            ...state,
            selected: SidecarMenu.ASSIGNMENTS,
            selectedAssignmentGuid: assignmentGuid,
        })
    ),
    on(MainSidecarActions.setAssignmentViewStates, (state, { viewStates }): SidecarState => {
        return {
            ...state,
            storedAssignmentViewStates: viewStates,
        };
    }),
    on(MainSidecarActions.setCurrentAssignmentViewState, (state, { guid }): SidecarState => {
        return {
            ...state,
            currentAssignmentViewStateGuid: guid || null,
        };
    }),
    on(MainSidecarActions.setAssignmentListGuids, (state, { assignmentGuids }): SidecarState => {
        return {
            ...state,
            assignmentListGuids: assignmentGuids,
        };
    }),
    on(MainSidecarActions.setTopicViewStates, (state, { viewStates }): SidecarState => {
        return {
            ...state,
            storedTopicViewStates: viewStates,
        };
    }),
    on(MainSidecarActions.setCurrentTopicViewState, (state, { guid }): SidecarState => {
        return {
            ...state,
            currentTopicViewStateGuid: guid || null,
        };
    }),
    on(MainSidecarActions.setTopicListGuids, (state, { topicGuids }): SidecarState => {
        return {
            ...state,
            topicListGuids: topicGuids,
        };
    }),
    on(MainSidecarActions.setRundownViewStates, (state, { viewStates }): SidecarState => {
        return {
            ...state,
            storedRundownViewStates: viewStates,
        };
    }),
    on(MainSidecarActions.setCurrentRundownViewState, (state, { guid }): SidecarState => {
        return {
            ...state,
            currentRundownViewStateGuid: guid || null,
        };
    }),
    on(MainSidecarActions.setRundownListGuids, (state, { rundownGuids }): SidecarState => {
        return {
            ...state,
            rundownListGuids: rundownGuids,
        };
    }),
    on(
        MainSidecarActions.selectSidecarStory,
        (state, { guid }): SidecarState => ({
            ...state,
            selected: SidecarMenu.STORIES,
            selectedStoryGuid: guid,
        })
    ),
    on(MainSidecarActions.setStoryViewStates, (state, { viewStates }): SidecarState => {
        return {
            ...state,
            storedStoryViewStates: viewStates,
        };
    }),
    on(MainSidecarActions.setCurrentStoryViewState, (state, { guid }): SidecarState => {
        return {
            ...state,
            currentStoryViewStateGuid: guid || null,
        };
    }),
    on(MainSidecarActions.setStoryListGuids, (state, { storyGuids }): SidecarState => {
        return {
            ...state,
            storyListGuids: storyGuids,
        };
    }),
    on(MainSidecarActions.selectSidecarRundown, (state, { guid }): SidecarState => {
        return {
            ...state,
            selectedRundownGuid: guid,
        };
    }),
    on(MainSidecarActions.selectSidecarTopic, (state, { guid }): SidecarState => {
        return {
            ...state,
            selectedTopicGuid: guid,
        };
    }),
    on(MainSidecarActions.setSelectedStoryHasUnsavedChanges, (state, { hasUnsavedChanges }): SidecarState => {
        return {
            ...state,
            selectedStoryHasUnsavedChanges: hasUnsavedChanges,
        };
    }),
    on(MainSidecarActions.setCurrentROSelectedStory, (state, { guid }): SidecarState => {
        return {
            ...state,
            currentRundownSelectedStoryGuid: guid,
        };
    }),
    on(MainSidecarActions.setCurrentTopicSelectedStory, (state, { guid }): SidecarState => {
        return {
            ...state,
            currentTopicSelectedStoryGuid: guid,
        };
    }),
    on(MainSidecarActions.setCurrentTopicSelectedAssignment, (state, { guid }): SidecarState => {
        return {
            ...state,
            currentTopicSelectedAssignmentGuid: guid,
        };
    }),
    on(
        MainSidecarActions.setCurrentMosItem,
        (state, { mosId: guid }): SidecarState => ({
            ...state,
            selectedMosItemID: guid,
        })
    ),
    on(
        MainSidecarActions.setAssignmentsListSearchType,
        (state, { searchType }): SidecarState => ({
            ...state,
            selectedAssignmentListSearchType: searchType,
        })
    ),
    on(
        MainSidecarActions.setStoriesListSearchType,
        (state, { searchType }): SidecarState => ({
            ...state,
            selectedStoryListSearchType: searchType,
        })
    ),
    on(
        MainSidecarActions.setTopicsListSearchType,
        (state, { searchType }): SidecarState => ({
            ...state,
            selectedTopicListSearchType: searchType,
        })
    ),
    on(
        MainSidecarActions.setRundownsListSearchType,
        (state, { searchType }): SidecarState => ({
            ...state,
            selectedRundownListSearchType: searchType,
        })
    )
);

// Second sidecar reducer
export const secondSidecarInitialState: SidecarState = {
    ...BASE_SIDECAR_INITIAL_STATE,
};

export const secondSidecarReducer = createReducer(
    secondSidecarInitialState,
    on(
        FullscreenActions.resetState,
        (): SidecarState => ({
            ...secondSidecarInitialState,
        })
    ),
    on(
        SecondSidecarActions.selectSidecarMenu,
        (state, { menu }): SidecarState => ({
            ...state,
            selected: menu,
        })
    ),
    on(
        SecondSidecarActions.selectSidecarAssignment,
        (state, { assignmentGuid }): SidecarState => ({
            ...state,
            selected: SidecarMenu.ASSIGNMENTS,
            selectedAssignmentGuid: assignmentGuid,
        })
    ),
    on(SecondSidecarActions.setAssignmentViewStates, (state, { viewStates }): SidecarState => {
        return {
            ...state,
            storedAssignmentViewStates: viewStates,
        };
    }),
    on(SecondSidecarActions.setCurrentAssignmentViewState, (state, { guid }): SidecarState => {
        return {
            ...state,
            currentAssignmentViewStateGuid: guid || null,
        };
    }),
    on(SecondSidecarActions.setAssignmentListGuids, (state, { assignmentGuids: topicGuids }): SidecarState => {
        return {
            ...state,
            assignmentListGuids: topicGuids,
        };
    }),
    on(SecondSidecarActions.setTopicViewStates, (state, { viewStates }): SidecarState => {
        return {
            ...state,
            storedTopicViewStates: viewStates,
        };
    }),
    on(SecondSidecarActions.setCurrentTopicViewState, (state, { guid }): SidecarState => {
        return {
            ...state,
            currentTopicViewStateGuid: guid || null,
        };
    }),
    on(SecondSidecarActions.setTopicListGuids, (state, { topicGuids }): SidecarState => {
        return {
            ...state,
            topicListGuids: topicGuids,
        };
    }),
    on(SecondSidecarActions.setRundownViewStates, (state, { viewStates }): SidecarState => {
        return {
            ...state,
            storedRundownViewStates: viewStates,
        };
    }),
    on(SecondSidecarActions.setCurrentRundownViewState, (state, { guid }): SidecarState => {
        return {
            ...state,
            currentRundownViewStateGuid: guid || null,
        };
    }),
    on(SecondSidecarActions.setRundownListGuids, (state, { rundownGuids }): SidecarState => {
        return {
            ...state,
            rundownListGuids: rundownGuids,
        };
    }),
    on(
        SecondSidecarActions.selectSidecarStory,
        (state, { guid }): SidecarState => ({
            ...state,
            selected: SidecarMenu.STORIES,
            selectedStoryGuid: guid,
        })
    ),
    on(SecondSidecarActions.setStoryViewStates, (state, { viewStates }): SidecarState => {
        return {
            ...state,
            storedStoryViewStates: viewStates,
        };
    }),
    on(SecondSidecarActions.setCurrentStoryViewState, (state, { guid }): SidecarState => {
        return {
            ...state,
            currentStoryViewStateGuid: guid || null,
        };
    }),
    on(SecondSidecarActions.setStoryListGuids, (state, { storyGuids }): SidecarState => {
        return {
            ...state,
            storyListGuids: storyGuids,
        };
    }),
    on(SecondSidecarActions.selectSidecarRundown, (state, { guid }): SidecarState => {
        return {
            ...state,
            selectedRundownGuid: guid,
        };
    }),
    on(SecondSidecarActions.selectSidecarTopic, (state, { guid }): SidecarState => {
        return {
            ...state,
            selectedTopicGuid: guid,
        };
    }),
    on(SecondSidecarActions.setSelectedStoryHasUnsavedChanges, (state, { hasUnsavedChanges }): SidecarState => {
        return {
            ...state,
            selectedStoryHasUnsavedChanges: hasUnsavedChanges,
        };
    }),
    on(SecondSidecarActions.setCurrentROSelectedStory, (state, { guid }): SidecarState => {
        return {
            ...state,
            currentRundownSelectedStoryGuid: guid,
        };
    }),
    on(SecondSidecarActions.setCurrentTopicSelectedStory, (state, { guid }): SidecarState => {
        return {
            ...state,
            currentTopicSelectedStoryGuid: guid,
        };
    }),
    on(SecondSidecarActions.setCurrentTopicSelectedAssignment, (state, { guid }): SidecarState => {
        return {
            ...state,
            currentTopicSelectedAssignmentGuid: guid,
        };
    }),
    on(
        SecondSidecarActions.setCurrentMosItem,
        (state, { mosId: guid }): SidecarState => ({
            ...state,
            selectedMosItemID: guid,
        })
    ),
    on(
        SecondSidecarActions.setAssignmentsListSearchType,
        (state, { searchType }): SidecarState => ({
            ...state,
            selectedAssignmentListSearchType: searchType,
        })
    ),
    on(
        SecondSidecarActions.setStoriesListSearchType,
        (state, { searchType }): SidecarState => ({
            ...state,
            selectedStoryListSearchType: searchType,
        })
    ),
    on(
        SecondSidecarActions.setTopicsListSearchType,
        (state, { searchType }): SidecarState => ({
            ...state,
            selectedTopicListSearchType: searchType,
        })
    ),
    on(
        SecondSidecarActions.setRundownsListSearchType,
        (state, { searchType }): SidecarState => ({
            ...state,
            selectedRundownListSearchType: searchType,
        })
    )
);

// Third sidecar reducer
export const thirdSidecarInitialState: SidecarState = {
    ...BASE_SIDECAR_INITIAL_STATE,
};

export const thirdSidecarReducer = createReducer(
    thirdSidecarInitialState,
    on(
        FullscreenActions.resetState,
        (): SidecarState => ({
            ...thirdSidecarInitialState,
        })
    ),
    on(
        ThirdSidecarActions.selectSidecarMenu,
        (state, { menu }): SidecarState => ({
            ...state,
            selected: menu,
        })
    ),
    on(
        ThirdSidecarActions.selectSidecarAssignment,
        (state, { assignmentGuid }): SidecarState => ({
            ...state,
            selected: SidecarMenu.ASSIGNMENTS,
            selectedAssignmentGuid: assignmentGuid,
        })
    ),
    on(ThirdSidecarActions.setAssignmentViewStates, (state, { viewStates }): SidecarState => {
        return {
            ...state,
            storedAssignmentViewStates: viewStates,
        };
    }),
    on(ThirdSidecarActions.setCurrentAssignmentViewState, (state, { guid }): SidecarState => {
        return {
            ...state,
            currentAssignmentViewStateGuid: guid || null,
        };
    }),
    on(ThirdSidecarActions.setAssignmentListGuids, (state, { assignmentGuids: topicGuids }): SidecarState => {
        return {
            ...state,
            assignmentListGuids: topicGuids,
        };
    }),
    on(ThirdSidecarActions.setTopicViewStates, (state, { viewStates }): SidecarState => {
        return {
            ...state,
            storedTopicViewStates: viewStates,
        };
    }),
    on(ThirdSidecarActions.setCurrentTopicViewState, (state, { guid }): SidecarState => {
        return {
            ...state,
            currentTopicViewStateGuid: guid || null,
        };
    }),
    on(ThirdSidecarActions.setTopicListGuids, (state, { topicGuids }): SidecarState => {
        return {
            ...state,
            topicListGuids: topicGuids,
        };
    }),
    on(ThirdSidecarActions.setRundownViewStates, (state, { viewStates }): SidecarState => {
        return {
            ...state,
            storedRundownViewStates: viewStates,
        };
    }),
    on(ThirdSidecarActions.setCurrentRundownViewState, (state, { guid }): SidecarState => {
        return {
            ...state,
            currentRundownViewStateGuid: guid || null,
        };
    }),
    on(ThirdSidecarActions.setRundownListGuids, (state, { rundownGuids }): SidecarState => {
        return {
            ...state,
            rundownListGuids: rundownGuids,
        };
    }),
    on(
        ThirdSidecarActions.selectSidecarStory,
        (state, { guid }): SidecarState => ({
            ...state,
            selected: SidecarMenu.STORIES,
            selectedStoryGuid: guid,
        })
    ),
    on(ThirdSidecarActions.setStoryViewStates, (state, { viewStates }): SidecarState => {
        return {
            ...state,
            storedStoryViewStates: viewStates,
        };
    }),
    on(ThirdSidecarActions.setCurrentStoryViewState, (state, { guid }): SidecarState => {
        return {
            ...state,
            currentStoryViewStateGuid: guid || null,
        };
    }),
    on(ThirdSidecarActions.setStoryListGuids, (state, { storyGuids }): SidecarState => {
        return {
            ...state,
            storyListGuids: storyGuids,
        };
    }),
    on(ThirdSidecarActions.selectSidecarRundown, (state, { guid }): SidecarState => {
        return {
            ...state,
            selectedRundownGuid: guid,
        };
    }),
    on(ThirdSidecarActions.selectSidecarTopic, (state, { guid }): SidecarState => {
        return {
            ...state,
            selectedTopicGuid: guid,
        };
    }),
    on(ThirdSidecarActions.setSelectedStoryHasUnsavedChanges, (state, { hasUnsavedChanges }): SidecarState => {
        return {
            ...state,
            selectedStoryHasUnsavedChanges: hasUnsavedChanges,
        };
    }),
    on(ThirdSidecarActions.setCurrentROSelectedStory, (state, { guid }): SidecarState => {
        return {
            ...state,
            currentRundownSelectedStoryGuid: guid,
        };
    }),
    on(ThirdSidecarActions.setCurrentTopicSelectedStory, (state, { guid }): SidecarState => {
        return {
            ...state,
            currentTopicSelectedStoryGuid: guid,
        };
    }),
    on(ThirdSidecarActions.setCurrentTopicSelectedAssignment, (state, { guid }): SidecarState => {
        return {
            ...state,
            currentTopicSelectedAssignmentGuid: guid,
        };
    }),
    on(
        ThirdSidecarActions.setCurrentMosItem,
        (state, { mosId: guid }): SidecarState => ({
            ...state,
            selectedMosItemID: guid,
        })
    ),
    on(
        ThirdSidecarActions.setAssignmentsListSearchType,
        (state, { searchType }): SidecarState => ({
            ...state,
            selectedAssignmentListSearchType: searchType,
        })
    ),
    on(
        ThirdSidecarActions.setStoriesListSearchType,
        (state, { searchType }): SidecarState => ({
            ...state,
            selectedStoryListSearchType: searchType,
        })
    ),
    on(
        ThirdSidecarActions.setTopicsListSearchType,
        (state, { searchType }): SidecarState => ({
            ...state,
            selectedTopicListSearchType: searchType,
        })
    ),
    on(
        ThirdSidecarActions.setRundownsListSearchType,
        (state, { searchType }): SidecarState => ({
            ...state,
            selectedRundownListSearchType: searchType,
        })
    )
);

// Fourth sidecar reducer
export const fourthSidecarInitialState: SidecarState = {
    ...BASE_SIDECAR_INITIAL_STATE,
};

export const fourthSidecarReducer = createReducer(
    fourthSidecarInitialState,
    on(
        FullscreenActions.resetState,
        (): SidecarState => ({
            ...fourthSidecarInitialState,
        })
    ),
    on(
        FourthSidecarActions.selectSidecarMenu,
        (state, { menu }): SidecarState => ({
            ...state,
            selected: menu,
        })
    ),
    on(
        FourthSidecarActions.selectSidecarAssignment,
        (state, { assignmentGuid }): SidecarState => ({
            ...state,
            selected: SidecarMenu.ASSIGNMENTS,
            selectedAssignmentGuid: assignmentGuid,
        })
    ),
    on(FourthSidecarActions.setAssignmentViewStates, (state, { viewStates }): SidecarState => {
        return {
            ...state,
            storedAssignmentViewStates: viewStates,
        };
    }),
    on(FourthSidecarActions.setCurrentAssignmentViewState, (state, { guid }): SidecarState => {
        return {
            ...state,
            currentAssignmentViewStateGuid: guid || null,
        };
    }),
    on(FourthSidecarActions.setAssignmentListGuids, (state, { assignmentGuids: topicGuids }): SidecarState => {
        return {
            ...state,
            assignmentListGuids: topicGuids,
        };
    }),
    on(FourthSidecarActions.setTopicViewStates, (state, { viewStates }): SidecarState => {
        return {
            ...state,
            storedTopicViewStates: viewStates,
        };
    }),
    on(FourthSidecarActions.setCurrentTopicViewState, (state, { guid }): SidecarState => {
        return {
            ...state,
            currentTopicViewStateGuid: guid || null,
        };
    }),
    on(FourthSidecarActions.setTopicListGuids, (state, { topicGuids }): SidecarState => {
        return {
            ...state,
            topicListGuids: topicGuids,
        };
    }),
    on(FourthSidecarActions.setRundownViewStates, (state, { viewStates }): SidecarState => {
        return {
            ...state,
            storedRundownViewStates: viewStates,
        };
    }),
    on(FourthSidecarActions.setCurrentRundownViewState, (state, { guid }): SidecarState => {
        return {
            ...state,
            currentRundownViewStateGuid: guid || null,
        };
    }),
    on(FourthSidecarActions.setRundownListGuids, (state, { rundownGuids }): SidecarState => {
        return {
            ...state,
            rundownListGuids: rundownGuids,
        };
    }),
    on(
        FourthSidecarActions.selectSidecarStory,
        (state, { guid }): SidecarState => ({
            ...state,
            selected: SidecarMenu.STORIES,
            selectedStoryGuid: guid,
        })
    ),
    on(FourthSidecarActions.setStoryViewStates, (state, { viewStates }): SidecarState => {
        return {
            ...state,
            storedStoryViewStates: viewStates,
        };
    }),
    on(FourthSidecarActions.setCurrentStoryViewState, (state, { guid }): SidecarState => {
        return {
            ...state,
            currentStoryViewStateGuid: guid || null,
        };
    }),
    on(FourthSidecarActions.setStoryListGuids, (state, { storyGuids }): SidecarState => {
        return {
            ...state,
            storyListGuids: storyGuids,
        };
    }),
    on(FourthSidecarActions.selectSidecarRundown, (state, { guid }): SidecarState => {
        return {
            ...state,
            selectedRundownGuid: guid,
        };
    }),
    on(FourthSidecarActions.selectSidecarTopic, (state, { guid }): SidecarState => {
        return {
            ...state,
            selectedTopicGuid: guid,
        };
    }),
    on(FourthSidecarActions.setSelectedStoryHasUnsavedChanges, (state, { hasUnsavedChanges }): SidecarState => {
        return {
            ...state,
            selectedStoryHasUnsavedChanges: hasUnsavedChanges,
        };
    }),
    on(FourthSidecarActions.setCurrentROSelectedStory, (state, { guid }): SidecarState => {
        return {
            ...state,
            currentRundownSelectedStoryGuid: guid,
        };
    }),
    on(FourthSidecarActions.setCurrentTopicSelectedStory, (state, { guid }): SidecarState => {
        return {
            ...state,
            currentTopicSelectedStoryGuid: guid,
        };
    }),
    on(FourthSidecarActions.setCurrentTopicSelectedAssignment, (state, { guid }): SidecarState => {
        return {
            ...state,
            currentTopicSelectedAssignmentGuid: guid,
        };
    }),
    on(
        FourthSidecarActions.setCurrentMosItem,
        (state, { mosId: guid }): SidecarState => ({
            ...state,
            selectedMosItemID: guid,
        })
    ),
    on(
        FourthSidecarActions.setAssignmentsListSearchType,
        (state, { searchType }): SidecarState => ({
            ...state,
            selectedAssignmentListSearchType: searchType,
        })
    ),
    on(
        FourthSidecarActions.setStoriesListSearchType,
        (state, { searchType }): SidecarState => ({
            ...state,
            selectedStoryListSearchType: searchType,
        })
    ),
    on(
        FourthSidecarActions.setTopicsListSearchType,
        (state, { searchType }): SidecarState => ({
            ...state,
            selectedTopicListSearchType: searchType,
        })
    ),
    on(
        FourthSidecarActions.setRundownsListSearchType,
        (state, { searchType }): SidecarState => ({
            ...state,
            selectedRundownListSearchType: searchType,
        })
    )
);
